<template>
  <v-content class="gb mk-page">
    <v-container fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" md="12" class="top-container">

          <!-- Page Header -->
          <div class="mk-header">
            <div>
              <h1 class="mk-header__title">
                <v-icon color="#0C2454" size="22" style="vertical-align: -3px; margin-right: 6px;">fas fa-bullhorn</v-icon>
                Marketing
              </h1>
              <p class="mk-header__subtitle">
                Watch the pages where homeowners ask for help, draft ready-to-paste replies, and track what converts.
              </p>
            </div>
            <v-btn rounded small dark color="#061D47"
              style="text-transform: none; font-weight: 600; height: 36px; padding: 0 16px;"
              @click="$router.push('/owner/clients')"
            >Homeowners</v-btn>
          </div>

          <!-- ── Pages to watch ─────────────────────────────────── -->
          <div class="mk-card">
            <div class="mk-card__head">
              <div>
                <h2 class="mk-card__title">Pages to watch</h2>
                <p class="mk-card__desc">Shared list of social pages your team reviews for homeowner “vendor request” posts.</p>
              </div>
              <span class="mk-badge">{{ activePages.length }}</span>
            </div>

            <!-- Add page form -->
            <div class="ss-form">
              <div class="ss-form__grid">
                <div class="ss-form__field">
                  <label class="ss-form__label">URL</label>
                  <input
                    v-model="newPage.url"
                    type="url"
                    placeholder="https://facebook.com/groups/…"
                    class="ss-input"
                    :class="{ 'ss-input--error': newPageUrlInvalid }"
                  />
                </div>
                <div class="ss-form__field">
                  <label class="ss-form__label">Label</label>
                  <input
                    v-model="newPage.label"
                    type="text"
                    placeholder="e.g. Austin Homeowners FB Group"
                    class="ss-input"
                  />
                </div>
                <div class="ss-form__field ss-form__field--btn">
                  <v-btn
                    color="#061D47" dark depressed block
                    class="mk-add-btn"
                    :loading="addingPage"
                    :disabled="!canAddPage"
                    @click="addPage"
                  >
                    <v-icon left size="16">fas fa-plus</v-icon>
                    Add
                  </v-btn>
                </div>
              </div>
              <!-- URL feedback: detected platform (valid) or inline error (invalid). -->
              <p v-if="newPageUrlInvalid" class="mk-field-error">Enter a valid link</p>
              <p v-else-if="newPage.url" class="mk-detected">
                Detected:
                <v-icon
                  :color="platformMeta(detectPlatform(newPage.url)).color"
                  size="14"
                  class="mk-detected__icon"
                >{{ platformMeta(detectPlatform(newPage.url)).icon }}</v-icon>
                <span>{{ platformMeta(detectPlatform(newPage.url)).label }}</span>
              </p>
            </div>

            <!-- Pages list -->
            <div v-if="loadingPages" class="mk-muted mk-pad">Loading pages…</div>
            <div v-else-if="!activePages.length" class="mk-muted mk-pad">
              No pages yet. Add the social pages your team should keep an eye on.
            </div>
            <ul v-else class="mk-page-list">
              <li v-for="page in activePages" :key="page.id" class="mk-page-row">
                <span class="mk-page-icon" :style="{ color: platformMeta(page.platform).color }">
                  <v-icon :color="platformMeta(page.platform).color" size="18">{{ platformMeta(page.platform).icon }}</v-icon>
                </span>
                <div class="mk-page-main">
                  <span class="mk-page-label">{{ page.label || platformMeta(page.platform).label }}</span>
                  <a :href="page.url" target="_blank" rel="noopener noreferrer" class="mk-page-url">{{ page.url }}</a>
                </div>
                <div class="mk-page-actions">
                  <v-btn small text color="#2F6FED" @click="useForReply(page)" title="Draft a reply for this platform">
                    <v-icon left size="14">fas fa-reply</v-icon>
                    Draft reply
                  </v-btn>
                  <v-btn icon small :href="page.url" target="_blank" rel="noopener noreferrer" title="Open page">
                    <v-icon size="16" color="#667085">fas fa-external-link-alt</v-icon>
                  </v-btn>
                  <v-btn icon small @click="openEditPage(page)" title="Edit">
                    <v-icon size="15" color="#667085">fas fa-pen</v-icon>
                  </v-btn>
                  <v-btn icon small @click="archivePage(page)" title="Archive">
                    <v-icon size="15" color="#C62828">fas fa-archive</v-icon>
                  </v-btn>
                </div>
              </li>
            </ul>
          </div>

          <!-- ── Screenshot → reply ─────────────────────────────── -->
          <div class="mk-card" ref="screenshotSection">
            <div class="mk-card__head">
              <div>
                <h2 class="mk-card__title">Screenshot → reply</h2>
                <p class="mk-card__desc">Drop a screenshot of a homeowner’s post and get an AI-drafted reply with a tracked referral link.</p>
              </div>
            </div>

            <div class="mk-shot-grid">
              <!-- Dropzone -->
              <div class="mk-shot-left">
                <div
                  class="mk-dropzone"
                  :class="{ 'is-dragging': dragging, 'has-image': !!imagePreview }"
                  tabindex="0"
                  @click="triggerFilePick"
                  @paste="onPaste"
                  @dragover.prevent="dragging = true"
                  @dragenter.prevent="dragging = true"
                  @dragleave.prevent="dragging = false"
                  @drop.prevent="onDrop"
                >
                  <template v-if="imagePreview">
                    <img :src="imagePreview" alt="screenshot preview" class="mk-dropzone__img" />
                    <button class="mk-dropzone__clear" @click.stop="clearImage" title="Remove image">×</button>
                  </template>
                  <template v-else>
                    <v-icon size="34" color="#9AA7BD">fas fa-cloud-upload-alt</v-icon>
                    <p class="mk-dropzone__hint">Drag &amp; drop, click to choose, or paste an image</p>
                    <p class="mk-dropzone__sub">PNG, JPG — a screenshot of the post works best</p>
                  </template>
                </div>
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*"
                  class="mk-hidden-input"
                  @change="onFileChange"
                />

                <div class="mk-shot-controls">
                  <div class="ss-form__field mk-shot-agent">
                    <label class="ss-form__label">Agent</label>
                    <select v-model="screenshot.agentId" class="ss-input ss-select">
                      <option value="" disabled>Select agent</option>
                      <option v-for="a in agents" :key="a.id" :value="a.id">{{ a.label }}</option>
                    </select>
                  </div>
                  <div class="ss-form__field mk-shot-platform">
                    <label class="ss-form__label">Platform</label>
                    <select v-model="screenshot.platform" class="ss-input ss-select">
                      <option v-for="p in platforms" :key="p.value" :value="p.value">{{ p.label }}</option>
                    </select>
                  </div>
                  <v-btn
                    color="#061D47" dark depressed
                    class="mk-generate-btn"
                    :loading="generating"
                    :disabled="!imageFile || generating || !screenshot.agentId"
                    @click="generateReply"
                  >
                    <v-icon left size="16">fas fa-magic</v-icon>
                    Generate reply
                  </v-btn>
                </div>
                <p v-if="screenshotError" class="mk-error">{{ screenshotError }}</p>
              </div>

              <!-- Result -->
              <div class="mk-shot-right">
                <div v-if="!reply && !generating" class="mk-result-empty">
                  <v-icon size="28" color="#C7D0E0">fas fa-comment-dots</v-icon>
                  <p>The drafted reply will appear here.</p>
                </div>
                <div v-else-if="generating" class="mk-result-empty">
                  <v-progress-circular indeterminate color="#061D47" size="28" width="3"></v-progress-circular>
                  <p>Reading the screenshot and drafting a reply…</p>
                </div>
                <div v-else class="mk-result">
                  <div class="mk-result-meta">
                    <v-chip v-if="reply.detected_category" small label color="#EEF4FF" text-color="#2F6FED" class="mk-chip">
                      {{ reply.detected_category }}
                    </v-chip>
                  </div>
                  <label class="mk-result-label">Reply (edit before pasting if you like)</label>
                  <textarea v-model="replyText" class="mk-reply-textarea" rows="7"></textarea>
                  <div class="mk-result-row">
                    <v-btn small depressed color="#2F6FED" dark @click="copyReply">
                      <v-icon left size="14">{{ replyCopied ? 'fas fa-check' : 'far fa-copy' }}</v-icon>
                      {{ replyCopied ? 'Copied' : 'Copy reply' }}
                    </v-btn>
                  </div>
                  <div v-if="reply.link" class="mk-link-row">
                    <span class="mk-link-label">Tracked link</span>
                    <a :href="reply.link" target="_blank" rel="noopener noreferrer" class="mk-link-url">{{ reply.link }}</a>
                    <v-btn icon small @click="copyText(reply.link)" title="Copy link">
                      <v-icon size="14" color="#667085">far fa-copy</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Dashboard (light) ──────────────────────────────── -->
          <div class="mk-card">
            <div class="mk-card__head">
              <div>
                <h2 class="mk-card__title">Dashboard</h2>
                <p class="mk-card__desc">AI-generated replies, link opens, sign-ups attributed, and open-to-sign-up conversion across categories, platforms, and agents.</p>
              </div>
              <v-btn icon small @click="fetchDashboard" :loading="loadingDashboard" title="Refresh">
                <v-icon size="16" color="#667085">fas fa-sync-alt</v-icon>
              </v-btn>
            </div>

            <div v-if="loadingDashboard && !dashboard" class="mk-muted mk-pad">Loading dashboard…</div>
            <template v-else>
              <!-- Totals -->
              <div class="mk-totals">
                <div class="mk-tile">
                  <span class="mk-tile__value">{{ formatNumber(totals.replies) }}</span>
                  <span class="mk-tile__label">
                    AI Generated Replies
                    <v-tooltip top max-width="260">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon size="12" color="#98A2B3" v-bind="attrs" v-on="on" style="cursor: help; margin-left: 3px;">fas fa-question-circle</v-icon>
                      </template>
                      <span>AI-drafted replies created with the Screenshot → reply tool.</span>
                    </v-tooltip>
                  </span>
                </div>
                <div class="mk-tile">
                  <span class="mk-tile__value">{{ formatNumber(totals.opens) }}</span>
                  <span class="mk-tile__label">
                    Opens
                    <v-tooltip top max-width="260">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon size="12" color="#98A2B3" v-bind="attrs" v-on="on" style="cursor: help; margin-left: 3px;">fas fa-question-circle</v-icon>
                      </template>
                      <span>Times the tracked links in those replies and source links were opened (clicked).</span>
                    </v-tooltip>
                  </span>
                </div>
                <div class="mk-tile">
                  <span class="mk-tile__value">{{ formatNumber(totals.signups) }}</span>
                  <span class="mk-tile__label">
                    Sign-ups
                    <v-tooltip top max-width="260">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon size="12" color="#98A2B3" v-bind="attrs" v-on="on" style="cursor: help; margin-left: 3px;">fas fa-question-circle</v-icon>
                      </template>
                      <span>Homeowners who signed up through a tracked link, credited to the agent who shared it.</span>
                    </v-tooltip>
                  </span>
                </div>
                <div class="mk-tile">
                  <span class="mk-tile__value">{{ formatPct(totals.conversion) }}</span>
                  <span class="mk-tile__label">
                    Open → Sign-up
                    <v-tooltip top max-width="260">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon size="12" color="#98A2B3" v-bind="attrs" v-on="on" style="cursor: help; margin-left: 3px;">fas fa-question-circle</v-icon>
                      </template>
                      <span>Share of link opens that turned into sign-ups (sign-ups ÷ opens).</span>
                    </v-tooltip>
                  </span>
                </div>
              </div>

              <!-- Breakdown tables -->
              <div class="mk-tables">
                <div class="mk-mini">
                  <h3 class="mk-mini__title">By category</h3>
                  <table class="mk-mini__table">
                    <thead><tr><th>Category</th><th class="num">AI Replies</th><th class="num">Opens</th><th class="num">Sign-ups</th></tr></thead>
                    <tbody>
                      <tr v-for="(r, i) in byCategory" :key="'cat-' + i">
                        <td>{{ r.category || '—' }}</td>
                        <td class="num">{{ formatNumber(r.replies) }}</td>
                        <td class="num">{{ formatNumber(r.opens) }}</td>
                        <td class="num">{{ formatNumber(r.signups) }}</td>
                      </tr>
                      <tr v-if="!byCategory.length"><td colspan="4" class="mk-muted">No data yet.</td></tr>
                    </tbody>
                  </table>
                </div>

                <div class="mk-mini">
                  <h3 class="mk-mini__title">By platform</h3>
                  <table class="mk-mini__table">
                    <thead><tr><th>Platform</th><th class="num">AI Replies</th><th class="num">Opens</th><th class="num">Sign-ups</th></tr></thead>
                    <tbody>
                      <tr v-for="(r, i) in byPlatform" :key="'plat-' + i">
                        <td>
                          <v-icon :color="platformMeta(r.platform).color" size="13" style="margin-right: 5px;">{{ platformMeta(r.platform).icon }}</v-icon>
                          {{ platformMeta(r.platform).label }}
                        </td>
                        <td class="num">{{ formatNumber(r.replies) }}</td>
                        <td class="num">{{ formatNumber(r.opens) }}</td>
                        <td class="num">{{ formatNumber(r.signups) }}</td>
                      </tr>
                      <tr v-if="!byPlatform.length"><td colspan="4" class="mk-muted">No data yet.</td></tr>
                    </tbody>
                  </table>
                </div>

                <div class="mk-mini">
                  <h3 class="mk-mini__title">By agent</h3>
                  <table class="mk-mini__table">
                    <thead><tr><th>Agent</th><th class="num">AI Replies</th><th class="num">Opens</th><th class="num">Sign-ups</th></tr></thead>
                    <tbody>
                      <tr v-for="(r, i) in byAgent" :key="'agent-' + i">
                        <td>{{ r.agent_name || r.agent_id || '—' }}</td>
                        <td class="num">{{ formatNumber(r.replies) }}</td>
                        <td class="num">{{ formatNumber(r.opens) }}</td>
                        <td class="num">{{ formatNumber(r.signups) }}</td>
                      </tr>
                      <tr v-if="!byAgent.length"><td colspan="4" class="mk-muted">No data yet.</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
          </div>

          <!-- ── Sign-up Links (folded in) ──────────────────────── -->
          <div class="mk-section-head">
            <h2 class="mk-card__title">Sign-up Links</h2>
            <p class="mk-card__desc">Create and track where your sign-ups are coming from.</p>
          </div>
          <SignupLinksSection :tenant-id="tenantId" :super-admin="superAdmin" />

        </v-col>
      </v-row>
    </v-container>

    <!-- Edit page dialog -->
    <v-dialog v-model="editDialog" max-width="480">
      <v-card>
        <v-card-title style="color: #061D47;">Edit watched page</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editPageDraft.url"
            label="URL"
            outlined dense
            hide-details="auto"
            class="mb-3"
            :error="editPageUrlInvalid"
            :error-messages="editPageUrlInvalid ? 'Enter a valid link' : []"
          ></v-text-field>
          <v-text-field v-model="editPageDraft.label" label="Label" outlined dense hide-details="auto" class="mb-3"></v-text-field>
          <p v-if="editPageDraft.url && !editPageUrlInvalid" class="mk-detected">
            Detected:
            <v-icon
              :color="platformMeta(detectPlatform(editPageDraft.url)).color"
              size="14"
              class="mk-detected__icon"
            >{{ platformMeta(detectPlatform(editPageDraft.url)).icon }}</v-icon>
            <span>{{ platformMeta(detectPlatform(editPageDraft.url)).label }}</span>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="editDialog = false">Cancel</v-btn>
          <v-btn
            color="#061D47" dark depressed
            :loading="savingPage"
            :disabled="!isValidUrl(editPageDraft.url)"
            @click="saveEditPage"
          >Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-content>
</template>

<script>
import { mapState } from 'vuex';
import SignupLinksSection from './SignupLinksSection.vue';
import {
  listWatchedPages,
  createWatchedPage,
  updateWatchedPage,
  archiveWatchedPage,
  generateScreenshotReply,
  getDashboard,
  listTenantAgents,
  humanizeError,
} from './marketing_api';

const PLATFORMS = [
  { value: 'facebook', label: 'Facebook', icon: 'fab fa-facebook', color: '#1877F2' },
  { value: 'instagram', label: 'Instagram', icon: 'fab fa-instagram', color: '#E4405F' },
  { value: 'reddit', label: 'Reddit', icon: 'fab fa-reddit', color: '#FF4500' },
  { value: 'nextdoor', label: 'Nextdoor', icon: 'fas fa-home', color: '#00B246' },
  { value: 'twitter', label: 'Twitter / X', icon: 'fab fa-twitter', color: '#1DA1F2' },
  { value: 'linkedin', label: 'LinkedIn', icon: 'fab fa-linkedin', color: '#0A66C2' },
  { value: 'other', label: 'Other', icon: 'fas fa-globe', color: '#6B7280' },
];

export default {
  name: 'Marketing',
  components: { SignupLinksSection },
  props: {
    // When set (super-user Marketing Console), every marketing call acts on
    // this tenant instead of the caller's own. Defaults preserve current
    // behavior for the normal owner/agent Marketing screen.
    tenantId: { type: String, default: '' },
    superAdmin: { type: Boolean, default: false },
  },
  data() {
    return {
      platforms: PLATFORMS,

      // Agent picker for the Screenshot → reply flow (and shared with the
      // sign-up links agent picker).
      agents: [],

      // Pages to watch
      pages: [],
      loadingPages: false,
      addingPage: false,
      newPage: { url: '', label: '' },
      editDialog: false,
      savingPage: false,
      editPageDraft: { id: '', url: '', label: '' },

      // Screenshot → reply
      dragging: false,
      imageFile: null,
      imagePreview: '',
      imageMediaType: '',
      screenshot: { platform: 'facebook', agentId: '' },
      generating: false,
      screenshotError: '',
      reply: null,
      replyText: '',
      replyCopied: false,

      // Dashboard
      dashboard: null,
      loadingDashboard: false,

      snackbar: { show: false, message: '', color: 'success' },
    };
  },
  computed: {
    ...mapState({
      user: state => state.Authenticate.user,
    }),
    activePages() {
      return (this.pages || []).filter(p => !p.archived);
    },
    canAddPage() {
      return this.isValidUrl(this.newPage.url) && !this.addingPage;
    },
    // True only when the user has typed something that isn't a usable link — so
    // the empty field doesn't show an error before the user has typed anything.
    newPageUrlInvalid() {
      return !!this.newPage.url && !this.isValidUrl(this.newPage.url);
    },
    editPageUrlInvalid() {
      return !!this.editPageDraft.url && !this.isValidUrl(this.editPageDraft.url);
    },
    totals() {
      return (this.dashboard && this.dashboard.totals) || { replies: 0, opens: 0, signups: 0, conversion: 0 };
    },
    byCategory() {
      return (this.dashboard && this.dashboard.by_category) || [];
    },
    byPlatform() {
      return (this.dashboard && this.dashboard.by_platform) || [];
    },
    byAgent() {
      return (this.dashboard && this.dashboard.by_agent) || [];
    },
  },
  mounted() {
    this.fetchPages();
    this.fetchDashboard();
    this.fetchAgents();
    // Convenience: allow pasting a screenshot anywhere on the page once the
    // user is on this view. Removed in beforeDestroy.
    document.addEventListener('paste', this.onPaste);
  },
  beforeDestroy() {
    document.removeEventListener('paste', this.onPaste);
    if (this.imagePreview) URL.revokeObjectURL(this.imagePreview);
  },
  methods: {
    platformMeta(value) {
      return PLATFORMS.find(p => p.value === value) || PLATFORMS[PLATFORMS.length - 1];
    },

    // Mirror of the backend's URL → platform detection (server is authoritative;
    // this is just for the read-only "Detected:" chip). Prepend a protocol when
    // missing so `new URL` can parse it, strip leading www./m., then map the
    // hostname to a known platform. Unknown/unparseable → 'other'.
    detectPlatform(url) {
      let host;
      try {
        const raw = String(url || '').trim();
        const withProto = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
        host = new URL(withProto).hostname.toLowerCase().replace(/^www\./, '').replace(/^m\./, '');
      } catch (_) {
        return 'other';
      }
      const map = [
        ['facebook', ['facebook.com', 'fb.com', 'fb.me']],
        ['instagram', ['instagram.com', 'instagr.am']],
        ['reddit', ['reddit.com', 'redd.it']],
        ['nextdoor', ['nextdoor.com']],
        ['twitter', ['twitter.com', 'x.com', 't.co']],
        ['linkedin', ['linkedin.com', 'lnkd.in']],
      ];
      for (const [platform, domains] of map) {
        if (domains.some(d => host === d || host.endsWith(`.${d}`))) return platform;
      }
      return 'other';
    },

    // A string is a usable link only if it parses as a URL (after prepending a
    // protocol when missing) AND has a dotted hostname — so "random text" is
    // rejected but "facebook.com/groups/1" is accepted.
    isValidUrl(str) {
      const raw = String(str || '').trim();
      if (!raw) return false;
      const withProto = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      try {
        return new URL(withProto).hostname.includes('.');
      } catch (_) {
        return false;
      }
    },

    // Prepend https:// when the user omitted the protocol, so the stored link is
    // clickable. Sent to the backend on add/save.
    normalizeUrl(str) {
      const raw = String(str || '').trim();
      if (!raw) return raw;
      return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    },

    notify(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    formatNumber(v) {
      const n = Number(v);
      if (!isFinite(n)) return '0';
      return n.toLocaleString('en-US');
    },

    // `conversion` is always a fraction from the backend (signups / replies),
    // and can legitimately exceed 1 when attributed sign-ups outnumber the
    // screenshot replies that produced them — so always scale by 100.
    formatPct(v) {
      const n = Number(v);
      if (!isFinite(n)) return '0%';
      return `${(n * 100).toFixed(1)}%`;
    },

    // ── Agents (Screenshot → reply picker) ─────────────────────
    async fetchAgents() {
      try {
        this.agents = await listTenantAgents({ tenantId: this.tenantId });
        // Own-tenant mode: default to the logged-in user (matches the previous
        // implicit agent_id used for screenshot replies). Super-admin mode:
        // leave blank — the Generate button stays disabled until an agent is
        // explicitly chosen.
        if (!this.superAdmin && !this.screenshot.agentId) {
          const defaultId = this.user && (this.user.owner_id || this.user.id);
          if (defaultId && this.agents.some(a => a.id === defaultId)) {
            this.screenshot.agentId = defaultId;
          }
        }
      } catch (e) {
        console.error('[marketing] failed to load agents', e);
        this.agents = [];
      }
    },

    // ── Pages to watch ─────────────────────────────────────────
    async fetchPages() {
      this.loadingPages = true;
      try {
        this.pages = await listWatchedPages({ tenantId: this.tenantId });
      } catch (e) {
        console.error('[marketing] failed to load watched pages', e);
        this.notify(`Couldn't load watched pages: ${humanizeError(e)}`, 'error');
      } finally {
        this.loadingPages = false;
      }
    },

    async addPage() {
      if (!this.canAddPage) return;
      this.addingPage = true;
      try {
        const page = await createWatchedPage({
          url: this.normalizeUrl(this.newPage.url),
          label: (this.newPage.label || '').trim(),
        }, { tenantId: this.tenantId });
        if (page) this.pages.push(page);
        else await this.fetchPages();
        this.newPage = { url: '', label: '' };
        this.notify('Page added.');
      } catch (e) {
        console.error('[marketing] failed to add page', e);
        this.notify(`Couldn't add page: ${humanizeError(e)}`, 'error');
      } finally {
        this.addingPage = false;
      }
    },

    openEditPage(page) {
      this.editPageDraft = {
        id: page.id,
        url: page.url || '',
        label: page.label || '',
      };
      this.editDialog = true;
    },

    async saveEditPage() {
      if (!this.isValidUrl(this.editPageDraft.url)) return;
      this.savingPage = true;
      try {
        const updated = await updateWatchedPage(this.editPageDraft.id, {
          url: this.normalizeUrl(this.editPageDraft.url),
          label: (this.editPageDraft.label || '').trim(),
        }, { tenantId: this.tenantId });
        const idx = this.pages.findIndex(p => p.id === this.editPageDraft.id);
        if (idx !== -1) {
          this.$set(this.pages, idx, updated || { ...this.pages[idx], ...this.editPageDraft });
        }
        this.editDialog = false;
        this.notify('Page updated.');
      } catch (e) {
        console.error('[marketing] failed to update page', e);
        this.notify(`Couldn't update page: ${humanizeError(e)}`, 'error');
      } finally {
        this.savingPage = false;
      }
    },

    async archivePage(page) {
      try {
        await archiveWatchedPage(page.id, { tenantId: this.tenantId });
        const idx = this.pages.findIndex(p => p.id === page.id);
        if (idx !== -1) this.$set(this.pages, idx, { ...this.pages[idx], archived: true });
        this.notify('Page archived.');
      } catch (e) {
        console.error('[marketing] failed to archive page', e);
        this.notify(`Couldn't archive page: ${humanizeError(e)}`, 'error');
      }
    },

    useForReply(page) {
      this.screenshot.platform = page.platform || 'other';
      const el = this.$refs.screenshotSection;
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    // ── Screenshot → reply ─────────────────────────────────────
    triggerFilePick() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.setImageFile(file);
      // Reset so picking the same file again still fires change.
      e.target.value = '';
    },
    onDrop(e) {
      this.dragging = false;
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this.setImageFile(file);
    },
    onPaste(e) {
      const items = (e.clipboardData && e.clipboardData.items) || [];
      for (const item of items) {
        if (item.kind === 'file' && item.type && item.type.indexOf('image/') === 0) {
          const file = item.getAsFile();
          if (file) {
            this.setImageFile(file);
            e.preventDefault();
          }
          break;
        }
      }
    },
    setImageFile(file) {
      if (!file.type || file.type.indexOf('image/') !== 0) {
        this.notify('Please choose an image file.', 'error');
        return;
      }
      if (this.imagePreview) URL.revokeObjectURL(this.imagePreview);
      this.imageFile = file;
      this.imageMediaType = file.type;
      this.imagePreview = URL.createObjectURL(file);
      this.screenshotError = '';
    },
    clearImage() {
      if (this.imagePreview) URL.revokeObjectURL(this.imagePreview);
      this.imageFile = null;
      this.imagePreview = '';
      this.imageMediaType = '';
    },

    // Read the File as a data URL, then strip the `data:<type>;base64,` prefix
    // so we send the raw base64 payload + its media type to the backend.
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          const match = /^data:(.*?);base64,(.*)$/.exec(result);
          if (match) resolve({ mediaType: match[1], base64: match[2] });
          else reject(new Error('Could not read the image.'));
        };
        reader.onerror = () => reject(reader.error || new Error('Could not read the image.'));
        reader.readAsDataURL(file);
      });
    },

    async generateReply() {
      if (!this.imageFile) return;
      this.generating = true;
      this.screenshotError = '';
      this.reply = null;
      try {
        const { base64, mediaType } = await this.fileToBase64(this.imageFile);
        const agentId = this.screenshot.agentId
          || (this.user && (this.user.owner_id || this.user.id))
          || undefined;
        const data = await generateScreenshotReply({
          image_base64: base64,
          image_media_type: mediaType || this.imageMediaType,
          platform: this.screenshot.platform,
          agent_id: agentId,
        }, { tenantId: this.tenantId });
        this.reply = data || {};
        this.replyText = (data && data.reply_text) || '';
        this.replyCopied = false;
      } catch (e) {
        console.error('[marketing] screenshot reply failed', e);
        this.screenshotError = humanizeError(e);
      } finally {
        this.generating = false;
      }
    },

    copyReply() {
      this.copyText(this.replyText);
      this.replyCopied = true;
      setTimeout(() => { this.replyCopied = false; }, 1800);
    },

    copyText(text) {
      if (!text) return;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).catch(() => this.fallbackCopy(text));
      } else {
        this.fallbackCopy(text);
      }
    },
    fallbackCopy(text) {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    },

    // ── Dashboard ──────────────────────────────────────────────
    async fetchDashboard() {
      this.loadingDashboard = true;
      try {
        this.dashboard = await getDashboard({ tenantId: this.tenantId });
      } catch (e) {
        console.error('[marketing] failed to load dashboard', e);
        this.notify(`Couldn't load dashboard: ${humanizeError(e)}`, 'error');
      } finally {
        this.loadingDashboard = false;
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

.mk-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ── Header ───────────────────────────────────────────── */
.mk-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 14px;
}

.mk-header__title {
  font-size: 22px;
  font-weight: 700;
  color: #0C2454;
  margin: 0;
  line-height: 28px;
}

.mk-header__subtitle {
  font-size: 13px;
  color: #5E6B85;
  margin: 2px 0 0;
  max-width: 720px;
}

/* ── Cards ────────────────────────────────────────────── */
.mk-card {
  background: #FFFFFF;
  border: 1px solid #E6ECF5;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
}

.mk-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 14px;
}

.mk-card__title {
  font-size: 15px;
  font-weight: 700;
  color: #0C2454;
  margin: 0;
}

.mk-card__desc {
  font-size: 13px;
  color: #6B7280;
  margin: 2px 0 0;
  max-width: 640px;
}

.mk-section-head {
  margin: 22px 0 8px;
}

.mk-badge {
  background: #EEF4FF;
  color: #2F6FED;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.mk-muted {
  color: #98A2B3;
  font-size: 13px;
}

.mk-pad {
  padding: 10px 2px;
}

.mk-error {
  color: #C62828;
  font-size: 12px;
  margin: 8px 0 0;
}

/* ── Watched pages form ──────────────────────────────── */
/* Watched-pages + screenshot form fields — same style as the "Create Source
   link" section (label above a native input/select), so the field title can't
   overlap the border the way Vuetify's outlined floating label did. */
.ss-form {
  margin-top: 0;
  margin-bottom: 14px;
}

.ss-form__grid {
  display: grid;
  grid-template-columns: 2fr 2fr 120px;
  gap: 8px;
  align-items: end;
}

.ss-form__field--btn {
  min-width: 120px;
}

.ss-form__label {
  display: block;
  font-size: 11px !important;
  font-weight: 600 !important;
  color: #344054 !important;
  margin: 0 0 4px;
  line-height: 1 !important;
  /* Neutralize global app.scss `label { transform: translateY(-18px);
     font-size: 15px; ... !important }` (Vuetify floating-label rule). */
  transform: none !important;
  max-width: 100% !important;
}

.ss-form__optional {
  font-weight: 400;
  color: #98A2B3;
}

.ss-input {
  width: 100%;
  height: 36px;
  border: 1.5px solid #D9E1F0;
  border-radius: 8px;
  padding: 0 10px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #14213D;
  outline: none;
  transition: border-color 120ms ease;
  background: #FFFFFF;
  box-sizing: border-box;
}

.ss-input:focus {
  border-color: #061D47;
}

.ss-input--error,
.ss-input--error:focus {
  border-color: #C62828;
}

/* URL feedback under the add form / inside the edit modal. */
.mk-field-error {
  margin: 8px 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #C62828;
}

.mk-detected {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 8px 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #667085;
}

.mk-detected__icon {
  margin: 0 1px;
}

.ss-select {
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2398A2B3' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 28px;
  cursor: pointer;
}

.mk-add-btn {
  height: 40px !important;
  text-transform: none;
  font-weight: 600;
  border-radius: 8px;
}

/* ── Watched pages list ──────────────────────────────── */
.mk-page-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.mk-page-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 6px;
  border-top: 1px solid #EEF2F7;
}

.mk-page-row:first-child {
  border-top: none;
}

.mk-page-icon {
  width: 28px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
}

.mk-page-main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.mk-page-label {
  font-size: 14px;
  font-weight: 600;
  color: #14213D;
}

.mk-page-url {
  font-size: 12px;
  color: #2F6FED;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 420px;
}

.mk-page-url:hover { text-decoration: underline; }

.mk-page-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.mk-page-actions .v-btn {
  text-transform: none;
}

/* ── Screenshot → reply ──────────────────────────────── */
.mk-shot-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}

.mk-dropzone {
  position: relative;
  border: 2px dashed #C7D4E6;
  border-radius: 12px;
  background: #FAFBFE;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  transition: all 120ms ease;
  outline: none;
  padding: 16px;
}

.mk-dropzone:hover,
.mk-dropzone:focus {
  border-color: #2F6FED;
  background: #F4F8FF;
}

.mk-dropzone.is-dragging {
  border-color: #2F6FED;
  background: #EAF2FF;
}

.mk-dropzone.has-image {
  padding: 8px;
  cursor: default;
}

.mk-dropzone__img {
  max-width: 100%;
  max-height: 320px;
  border-radius: 8px;
  display: block;
}

.mk-dropzone__clear {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: rgba(6, 29, 71, 0.78);
  color: #fff;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mk-dropzone__hint {
  font-size: 14px;
  font-weight: 600;
  color: #44516B;
  margin: 10px 0 2px;
}

.mk-dropzone__sub {
  font-size: 12px;
  color: #98A2B3;
  margin: 0;
}

.mk-hidden-input {
  display: none;
}

.mk-shot-controls {
  display: flex;
  gap: 10px;
  align-items: flex-end;
  margin-top: 12px;
}

.mk-shot-agent {
  flex: 1;
}

.mk-shot-platform {
  flex: 1;
}


.mk-generate-btn {
  height: 40px !important;
  text-transform: none;
  font-weight: 600;
  border-radius: 8px;
}

.mk-shot-right {
  display: flex;
  flex-direction: column;
}

.mk-result-empty {
  flex: 1;
  min-height: 200px;
  border: 1px solid #EEF2F7;
  border-radius: 12px;
  background: #FAFBFE;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #98A2B3;
  font-size: 13px;
  text-align: center;
  padding: 16px;
}

.mk-result {
  display: flex;
  flex-direction: column;
}

.mk-result-meta {
  margin-bottom: 8px;
  min-height: 26px;
}

.mk-chip {
  font-weight: 600;
  text-transform: capitalize;
}

.mk-result-label {
  display: block;
  font-size: 11px !important;
  font-weight: 600 !important;
  color: #344054 !important;
  margin-bottom: 4px;
  /* Neutralize the global app.scss `label { transform: translateY(-18px);
     font-size: 15px; ... !important }` (a Vuetify floating-label rule) that
     otherwise lifts this plain label up onto the category chip above it and
     forces the wrong size. */
  transform: none !important;
  max-width: 100% !important;
  line-height: 1.3 !important;
}

.mk-reply-textarea {
  width: 100%;
  border: 1.5px solid #D9E1F0;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: #14213D;
  resize: vertical;
  outline: none;
  background: #FFFFFF;
  box-sizing: border-box;
}

.mk-reply-textarea:focus {
  border-color: #2F6FED;
  box-shadow: 0 0 0 2px rgba(47, 111, 237, 0.08);
}

.mk-result-row {
  margin-top: 10px;
}

.mk-result-row .v-btn,
.mk-link-row .v-btn {
  text-transform: none;
}

.mk-link-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 8px 10px;
  background: #F7F9FD;
  border: 1px solid #E6ECF5;
  border-radius: 8px;
}

.mk-link-label {
  font-size: 11px;
  font-weight: 700;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}

.mk-link-url {
  font-size: 12px;
  color: #2F6FED;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.mk-link-url:hover { text-decoration: underline; }

/* ── Dashboard ───────────────────────────────────────── */
.mk-totals {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.mk-tile {
  background: #F7F9FD;
  border: 1px solid #E6ECF5;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mk-tile__value {
  font-size: 26px;
  font-weight: 700;
  color: #0C2454;
  line-height: 1;
}

.mk-tile__label {
  font-size: 12px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.mk-tables {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

.mk-mini__title {
  font-size: 13px;
  font-weight: 700;
  color: #0C2454;
  margin: 0 0 6px;
}

.mk-mini__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.mk-mini__table th {
  text-align: left;
  color: #6B7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: 10px;
  padding: 6px 8px;
  border-bottom: 1px solid #EEF2F7;
}

.mk-mini__table td {
  padding: 6px 8px;
  border-top: 1px solid #F4F6FB;
  color: #344054;
}

.mk-mini__table .num {
  text-align: right;
  white-space: nowrap;
}

@media (max-width: 960px) {
  .ss-form__grid {
    grid-template-columns: 1fr;
  }
  .mk-shot-grid {
    grid-template-columns: 1fr;
  }
  .mk-tables {
    grid-template-columns: 1fr;
  }
  .mk-header {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
